import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as complianceSummaryApi from '../api/complianceSummary';

export function ComplianceDashboard() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('program-nevi');

  const loadSummary = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await complianceSummaryApi.getComplianceSummary(selectedOrg.id, selectedProgram);
      setSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, selectedProgram]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view compliance data.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Compliance Center"
        subtitle="Overview of compliance status and deadlines"
      />

      <div style={styles.toolbar}>
        <select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          style={styles.select}
          data-testid="program-filter"
        >
          <option value="program-nevi">NEVI</option>
          <option value="program-fleet">Fleet</option>
        </select>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading compliance summary...</div>
      ) : summary && (
        <>
          <div style={styles.tilesGrid}>
            <div 
              style={styles.tile} 
              onClick={() => navigate('/compliance/checklist')}
              data-testid="completeness-tile"
            >
              <div style={styles.tileHeader}>BABA Completeness</div>
              <div style={styles.tileValue}>
                <span style={{
                  color: summary.babaCompleteness.percent >= 80 ? '#276749' : 
                         summary.babaCompleteness.percent >= 50 ? '#975a16' : '#c53030'
                }}>
                  {summary.babaCompleteness.percent}%
                </span>
              </div>
              <div style={styles.tileSubtext}>
                {summary.babaCompleteness.completed} of {summary.babaCompleteness.required} complete
              </div>
              {summary.babaCompleteness.missingCount > 0 && (
                <div style={styles.tileBadge}>
                  {summary.babaCompleteness.missingCount} missing
                </div>
              )}
            </div>

            <div 
              style={styles.tile}
              onClick={() => navigate('/compliance/baba')}
              data-testid="last-run-tile"
            >
              <div style={styles.tileHeader}>Last Packet Run</div>
              {summary.lastRun ? (
                <>
                  <div style={styles.tileValue}>
                    <span style={{ color: summary.lastRun.status === 'completed' ? '#276749' : '#975a16' }}>
                      {summary.lastRun.status}
                    </span>
                  </div>
                  <div style={styles.tileSubtext}>
                    {summary.lastRun.documentCount} documents
                  </div>
                  <div style={styles.tileDate}>
                    {new Date(summary.lastRun.createdAt).toLocaleDateString()}
                  </div>
                </>
              ) : (
                <div style={styles.tileEmpty}>No packets generated</div>
              )}
            </div>

            <div 
              style={styles.tile}
              data-testid="deadline-tile"
            >
              <div style={styles.tileHeader}>Next Deadline</div>
              {summary.nextDeadline ? (
                <>
                  <div style={styles.tileValue}>
                    <span style={{
                      color: summary.nextDeadline.daysRemaining <= 7 ? '#c53030' :
                             summary.nextDeadline.daysRemaining <= 30 ? '#975a16' : '#276749'
                    }}>
                      {summary.nextDeadline.daysRemaining} days
                    </span>
                  </div>
                  <div style={styles.tileSubtext}>{summary.nextDeadline.name}</div>
                  <div style={styles.tileDate}>{summary.nextDeadline.dueDate}</div>
                </>
              ) : (
                <div style={styles.tileEmpty}>No upcoming deadlines</div>
              )}
            </div>

            <div 
              style={styles.tile}
              onClick={() => navigate('/compliance/baba')}
              data-testid="packets-tile"
            >
              <div style={styles.tileHeader}>Total Packets</div>
              <div style={styles.tileValue}>{summary.totalPackets}</div>
              <div style={styles.tileSubtext}>Generated packets</div>
            </div>
          </div>

          <div style={styles.sectionsGrid}>
            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Upcoming Deadlines</h3>
              </div>
              <table style={styles.table} data-testid="deadlines-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Report</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Due Date</th>
                    <th style={styles.th}>Days Left</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.upcomingDeadlines.map(deadline => (
                    <tr key={deadline.id} style={styles.tr}>
                      <td style={styles.td}>{deadline.name}</td>
                      <td style={styles.td}>
                        <span style={styles.typeBadge}>{deadline.type}</span>
                      </td>
                      <td style={styles.td}>{deadline.dueDate}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.daysBadge,
                          background: deadline.daysRemaining <= 7 ? '#fed7d7' :
                                     deadline.daysRemaining <= 30 ? '#fefcbf' : '#c6f6d5',
                          color: deadline.daysRemaining <= 7 ? '#c53030' :
                                 deadline.daysRemaining <= 30 ? '#975a16' : '#276749'
                        }}>
                          {deadline.daysRemaining} days
                        </span>
                      </td>
                    </tr>
                  ))}
                  {summary.upcomingDeadlines.length === 0 && (
                    <tr>
                      <td colSpan="4" style={styles.emptyState}>No upcoming deadlines</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={styles.section}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>Quick Links</h3>
              </div>
              <div style={styles.quickLinks}>
                <button
                  onClick={() => navigate('/compliance/documents')}
                  style={styles.quickLink}
                  data-testid="link-documents"
                >
                  Document Library
                </button>
                <button
                  onClick={() => navigate('/compliance/checklist')}
                  style={styles.quickLink}
                  data-testid="link-checklist"
                >
                  Requirements Checklist
                </button>
                <button
                  onClick={() => navigate('/compliance/baba')}
                  style={styles.quickLink}
                  data-testid="link-packets"
                >
                  BABA Packets
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    maxWidth: '1400px',
    margin: '0 auto'
  },
  accessDenied: {
    padding: '3rem',
    textAlign: 'center',
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  toolbar: {
    marginBottom: '1.5rem'
  },
  select: {
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    background: 'white'
  },
  error: {
    padding: '1rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '8px',
    marginBottom: '1rem'
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#718096'
  },
  tilesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1.5rem',
    marginBottom: '2rem'
  },
  tile: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s'
  },
  tileHeader: {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    marginBottom: '0.5rem'
  },
  tileValue: {
    fontSize: '2.5rem',
    fontWeight: '700',
    marginBottom: '0.25rem'
  },
  tileSubtext: {
    fontSize: '0.875rem',
    color: '#718096'
  },
  tileDate: {
    fontSize: '0.75rem',
    color: '#a0aec0',
    marginTop: '0.25rem'
  },
  tileBadge: {
    display: 'inline-block',
    marginTop: '0.5rem',
    padding: '0.25rem 0.5rem',
    background: '#fed7d7',
    color: '#c53030',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  tileEmpty: {
    color: '#a0aec0',
    fontSize: '0.875rem',
    marginTop: '1rem'
  },
  sectionsGrid: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '1.5rem'
  },
  section: {
    background: 'white',
    borderRadius: '8px',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  sectionHeader: {
    marginBottom: '1rem'
  },
  sectionTitle: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '600',
    color: '#2d3748'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem',
    background: '#f7fafc',
    borderBottom: '1px solid #e2e8f0',
    fontWeight: '600',
    fontSize: '0.75rem',
    color: '#4a5568'
  },
  tr: {
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.875rem'
  },
  typeBadge: {
    padding: '0.25rem 0.5rem',
    background: '#e2e8f0',
    borderRadius: '4px',
    fontSize: '0.75rem',
    textTransform: 'capitalize'
  },
  daysBadge: {
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: '500'
  },
  emptyState: {
    padding: '1rem',
    textAlign: 'center',
    color: '#718096'
  },
  quickLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  quickLink: {
    padding: '0.75rem 1rem',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
    background: 'white',
    fontSize: '0.875rem',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.15s'
  }
};
